import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';

import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { AccessRoleDTO, AccessRoleFilterDTO, ListDTO, ListFilterDTO, ValueListDTO, ValueListFilterDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField, IChipsSettingProps } from '../../../shared/components';
import { ValueListMaintenance } from './ValueListMaintenance';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId, LocalStorange } from '../../../shared/environment';

interface IValueList {
    data: any;
}

export const ValueList: React.FC<IValueList> = ({data}) => {

    const [listList, setListList] = useState<ListDTO[]>([]);
    const [valueListValueList, setValueListValueList] = useState<ValueListDTO[]>([]);
    const [valueListFilterDTO, setValueListFilterDTO] = useState<ValueListFilterDTO>();
    
    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setValueListFilterDTO({ 
            ...valueListFilterDTO, value: '', 
            listId: data.id ?? '' 
        } as ValueListFilterDTO);
    }, []);
    
    useEffect(() => {
        handleListGetAllAsync();
        handleValueListGetAllViewAsync();

        handleGetPermission(ListId.parmsphere.ValueListId, setAccessRolePermissionDTO);

    }, [valueListFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleValueListGetAllViewAsync();
    }, [actionExecuted]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync',accessRoleFilterDTO, setActionExecuted, setLoading, undefined,  setList);
    }, []);
    
    const handleListGetAllAsync = useCallback(async () => {
        const listFilterDTO: ListFilterDTO = {
            name: '',
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('parmsphere.list', listFilterDTO, setLoading, setListList, showMessage);
    }, []);

    const handleValueListGetAllViewAsync = useCallback(async () => {
        if (valueListFilterDTO === undefined)
            return;
        if(accessRolePermissionDTO?.allowGetAllView == undefined)
            return; 
        if(!accessRolePermissionDTO?.allowGetAllView) {
            showMessage('There is no permission for get all view.', 'info');
            return;
        }
        GenericService.GetAllViewAsync1('parmsphere.valueList', valueListFilterDTO, setActionExecuted, setLoading, setValueListValueList, showMessage, true);
    }, [valueListFilterDTO, accessRolePermissionDTO]);

    const handleClearFilterClick = () => {
        setValueListFilterDTO({ ...valueListFilterDTO, value: '', listId: '' } as ValueListFilterDTO);
    };

    function handleSearchClick() {
        handleValueListGetAllViewAsync();
    }

    const handleNew = () => {
        if(!accessRolePermissionDTO?.allowAdd) {
            showMessage('There is no permission for new record.', 'info');
            return;
        }
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        if(!accessRolePermissionDTO?.allowGetById) {
            showMessage('There is no permission for view details.', 'info');
            return;
        }
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        if(!accessRolePermissionDTO?.allowDelete) {
            showMessage('There is no permission for delete record.', 'info');
            return;
        }
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: 'New ValueList', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: 'View Details', icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: 'Delete', icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: 'Total Records', value: valueListValueList.length },
        { label: 'Value', value: valueListFilterDTO?.value },
        { label: 'List', value: valueListFilterDTO?.listId },
    ];

    const chipIsSystem : IChipsSettingProps[] = [
        { value: 'true', iconName: 'task_alt_outlined', iconColor: 'success' },
        { value: 'false', iconName: 'block_outlined', iconColor: 'warning' },
    ];
    
    const columns: ICardColumns[] = [
        { field: 'value', label: 'Value', size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'valueIndex', label: 'Value Index', size: 1, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'isSystem', label: 'Is System', size: 1, type: 'boolean', visible: true, isChip: true, chipSettings: chipIsSystem, hasFieldLine: false, hasToolTip: true },
        { field: 'description', label: 'Description', size: 7, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='CANDIDATE'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='value'
                                label='Value'
                                setValue={setValueListFilterDTO}
                                value={valueListFilterDTO?.value}
                                obj={valueListFilterDTO}
                                placeholder={'Value'}
                                maxLength={200}
                            />
                        </Grid>
                        {!data && (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <SelectField
                                        id='listId'
                                        label='List Name'
                                        setValue={setValueListFilterDTO}
                                        value={valueListFilterDTO?.listId}
                                        obj={valueListFilterDTO}
                                        valueList={listList}
                                        hasNoneSelect={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        )}
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <Box sx={{ display: 'flex', maxHeight: '79vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <CardList
                        columns={columns}
                        rows={valueListValueList}
                        actions={cardActions}
                        hasChildren={false}
                        setRowExpanded={() => void undefined}
                        cardSize={12}
                    />
                </Box>
                {newRecord && (
                    <ValueListMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                        parentId={valueListFilterDTO?.listId}
                    />
                )}
                {editRecord && (
                    <ValueListMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'parmsphere.valueList'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />)}
            </LayoutDashboard>
        </React.Fragment >
    );
};